<script lang="ts">
  import type { DiagnosticMetrics, AnyMetric } from '$core/metrics/metrics.types';
  import type { CanonicalDataset, DiagnosticAssumptions } from '$core/domain/canonical.types';
  import { formatPercentage, formatMinutes, formatCurrencyBrl } from '$core/format/numberFormat';
  import MetricTile from '../../components/MetricTile.svelte';
  import Card from '../../components/Card.svelte';
  import EmptyState from '../../components/EmptyState.svelte';
  import { appStore } from '../../app/store';
  import AbsenceChart from './charts/AbsenceChart.svelte';
  import { buildDailyJourneys } from '$core/metrics/journeys';
  import {
    buildDailyAbsenceAnalytics,
    calcAbsenceChartKpis,
  } from '$core/metrics/dailyAbsenceAnalytics';

  export let metrics: DiagnosticMetrics;
  export let dataset: CanonicalDataset | undefined = undefined;
  export let assumptions: DiagnosticAssumptions | undefined = undefined;

  /** IDs de métricas com drill-down disponível. */
  const DRILL_DOWN_IDS = new Set([
    'absent.short_journeys',
    'absent.faltas',
    'cost.excess_minutes',
    'cost.cost_overtime',
    'cost.cost_atrasos',
    'cost.cost_absences',
  ]);

  /** Formata número com 1 casa decimal e vírgula. */
  function formatNum(v: number): string {
    return v.toFixed(1).replace('.', ',').replace(/,0$/, '');
  }

  function openDrillDown(metricCode: string): void {
    if (DRILL_DOWN_IDS.has(metricCode)) {
      appStore.openDrillDown(metricCode);
    }
  }

  let expandedSections: Record<string, boolean> = {
    visibility: true,
    cost: true,
    absence: true,
    rework: true,
    risk: true,
  };

  function toggleSection(id: string): void {
    expandedSections[id] = !expandedSections[id];
  }

  function formatMetricValue(metric: AnyMetric): string {
    switch (metric.kind) {
      case 'count': return String(metric.value);
      case 'percentage': return formatPercentage(metric.value);
      case 'minutes': return formatMinutes(metric.value);
      case 'money': return formatCurrencyBrl(metric.value);
      case 'occurrence': return `${metric.value} (${metric.affectedEmployees} colaboradores)`;
      case 'ranked': return `${metric.items.length} itens`;
      default: return '';
    }
  }

  /**
   * Mostra o aviso de feriados quando as escalas são sintéticas OU quando
   * o dataset existe (pois sempre aplicamos feriados nacionais no cálculo).
   */
  $: showHolidayDisclaimer = dataset !== undefined || true;

  // =========================================================================
  // Dados para o AbsenceChart (% diárias de faltas e jornadas curtas)
  // =========================================================================
  $: absenceChartPoints = (dataset !== undefined && assumptions !== undefined)
    ? buildDailyAbsenceAnalytics(
        buildDailyJourneys(dataset, assumptions),
        assumptions.toleranceMinutes,
      )
    : [];

  $: absenceChartKpis = calcAbsenceChartKpis(absenceChartPoints);
</script>

<div class="metrics-sections">

  <!-- Visibilidade -->
  <Card>
    <div class="section-header">
      <button
        class="section-toggle"
        aria-expanded={expandedSections['visibility']}
        on:click={() => toggleSection('visibility')}
      >
        <span class="section-title">Visibilidade</span>
        <span class="section-toggle__icon" aria-hidden="true">
          {expandedSections['visibility'] ? '▲' : '▼'}
        </span>
      </button>
    </div>
    {#if expandedSections['visibility']}
      <div class="section-body">
        <div class="metrics-grid">
          <MetricTile
            label={metrics.visibility.analyzedEmployees.label}
            value={formatMetricValue(metrics.visibility.analyzedEmployees)}
            helper={metrics.visibility.analyzedEmployees.helper}
            status="neutral"
          />
          <MetricTile
            label={metrics.visibility.analyzedDays.label}
            value={formatMetricValue(metrics.visibility.analyzedDays)}
            helper={metrics.visibility.analyzedDays.helper}
            status="neutral"
          />
          <MetricTile
            label={metrics.visibility.totalPunches.label}
            value={formatMetricValue(metrics.visibility.totalPunches)}
            helper={metrics.visibility.totalPunches.helper}
            status="neutral"
          />
          <MetricTile
            label={metrics.visibility.completeJourneys.label}
            value={formatMetricValue(metrics.visibility.completeJourneys)}
            helper={metrics.visibility.completeJourneys.helper}
            status="ok"
          />
          <MetricTile
            label={metrics.visibility.pendingJourneys.label}
            value={formatMetricValue(metrics.visibility.pendingJourneys)}
            helper={metrics.visibility.pendingJourneys.helper}
            status={metrics.visibility.pendingJourneys.value > 0 ? 'warning' : 'ok'}
          />
          {#if metrics.visibility.offlinePunchUsage !== null}
            <MetricTile
              label={metrics.visibility.offlinePunchUsage.label}
              value={formatMetricValue(metrics.visibility.offlinePunchUsage)}
              helper={metrics.visibility.offlinePunchUsage.helper}
              status="neutral"
            />
          {/if}
        </div>
      </div>
    {/if}
  </Card>

  <!-- Custo -->
  <Card>
    <div class="section-header">
      <button
        class="section-toggle"
        aria-expanded={expandedSections['cost']}
        on:click={() => toggleSection('cost')}
      >
        <span class="section-title">Custo</span>
        <span class="section-toggle__icon" aria-hidden="true">
          {expandedSections['cost'] ? '▲' : '▼'}
        </span>
      </button>
    </div>
    {#if expandedSections['cost']}
      <div class="section-body">
        <!-- Saldo bruto / Excesso / Déficit (E2 — narrativa equilibrada) -->
        {#if metrics.cost.deficitMinutes && metrics.cost.deficitMinutes.value > 0}
          <div class="balance-trio" data-testid="balance-trio">
            <p class="balance-trio__intro">
              <strong>Saldo bruto vs excesso reportado:</strong> a diferença vem de não-compensação inter-colaborador. O excesso de uns NÃO cancela o déficit de outros — só vale onde a CCT permite.
            </p>
            <div class="metrics-grid balance-trio__grid">
              <MetricTile
                label={metrics.cost.grossBalanceMinutes.label}
                value={formatMetricValue(metrics.cost.grossBalanceMinutes)}
                helper={metrics.cost.grossBalanceMinutes.helper}
                status="neutral"
              />
              <MetricTile
                label={metrics.cost.excessMinutes.label}
                value={formatMetricValue(metrics.cost.excessMinutes)}
                helper={metrics.cost.excessMinutes.helper}
                status="warning"
              />
              <MetricTile
                label={metrics.cost.deficitMinutes.label}
                value={formatMetricValue(metrics.cost.deficitMinutes)}
                helper={metrics.cost.deficitMinutes.helper}
                status="neutral"
              />
            </div>
          </div>
        {/if}

        <div class="metrics-grid">
          <MetricTile
            label={metrics.cost.excessMinutes.label}
            value={formatMetricValue(metrics.cost.excessMinutes)}
            helper={metrics.cost.excessMinutes.helper}
            status="warning"
            drillDownId="cost.excess_minutes"
            on:click={() => openDrillDown('cost.excess_minutes')}
          />
          <MetricTile
            label={metrics.cost.excessMinutesPerEmployee.label}
            value={formatMetricValue(metrics.cost.excessMinutesPerEmployee)}
            helper={metrics.cost.excessMinutesPerEmployee.helper}
            status="warning"
          />
          <MetricTile
            label={metrics.cost.excessMinutesPerEmployeePerDay.label}
            value={formatMetricValue(metrics.cost.excessMinutesPerEmployeePerDay)}
            helper={metrics.cost.excessMinutesPerEmployeePerDay.helper}
            status="warning"
          />
          <MetricTile
            label={metrics.cost.excessMinutesPerEmployeePerMonth.label}
            value={formatMetricValue(metrics.cost.excessMinutesPerEmployeePerMonth)}
            helper={metrics.cost.excessMinutesPerEmployeePerMonth.helper}
            status="warning"
          />
          {#if metrics.cost.costAtrasos !== null}
            <MetricTile
              label={metrics.cost.costAtrasos.label}
              value={formatMetricValue(metrics.cost.costAtrasos)}
              helper={metrics.cost.costAtrasos.helper}
              status="warning"
              drillDownId="cost.cost_atrasos"
              on:click={() => openDrillDown('cost.cost_atrasos')}
            />
          {/if}
          {#if metrics.cost.costOvertime !== null}
            <MetricTile
              label={metrics.cost.costOvertime.label}
              value={formatMetricValue(metrics.cost.costOvertime)}
              helper={metrics.cost.costOvertime.helper}
              status="error"
              drillDownId="cost.cost_overtime"
              on:click={() => openDrillDown('cost.cost_overtime')}
            />
          {/if}
          {#if metrics.cost.costAbsences !== null}
            <MetricTile
              label={metrics.cost.costAbsences.label}
              value={formatMetricValue(metrics.cost.costAbsences)}
              helper={metrics.cost.costAbsences.helper}
              status="warning"
              drillDownId="cost.cost_absences"
              on:click={() => openDrillDown('cost.cost_absences')}
            />
          {/if}
          {#if metrics.cost.costCorrection !== null}
            <MetricTile
              label={metrics.cost.costCorrection.label}
              value={formatMetricValue(metrics.cost.costCorrection)}
              helper={metrics.cost.costCorrection.helper}
              status="warning"
            />
          {/if}
          {#if metrics.cost.costTotal !== null}
            <MetricTile
              label={metrics.cost.costTotal.label}
              value={formatMetricValue(metrics.cost.costTotal)}
              helper={metrics.cost.costTotal.helper}
              status="error"
            />
          {/if}
          {#if metrics.cost.costTotalPerEmployee !== null}
            <MetricTile
              label={metrics.cost.costTotalPerEmployee.label}
              value={formatMetricValue(metrics.cost.costTotalPerEmployee)}
              helper={metrics.cost.costTotalPerEmployee.helper}
              status="error"
            />
          {/if}
        </div>
        {#if metrics.cost.costAtrasos === null && metrics.cost.costOvertime === null}
          <p class="metrics-sections__cost-cta">
            Para ver a tradução em <strong>R$</strong> dos minutos excedentes acima,
            preencha o valor-hora ou o salário bruto médio nas premissas.
            <button
              type="button"
              class="metrics-sections__cost-link"
              on:click={() => appStore.setCurrentStep('assumptions')}
            >
              Editar premissas →
            </button>
          </p>
        {/if}
        {#if showHolidayDisclaimer}
          <p class="metrics-sections__holiday-note">
            Apenas feriados nacionais brasileiros foram considerados. Feriados estaduais/municipais e bancos de horas não estão computados.
          </p>
        {/if}
      </div>
    {/if}
  </Card>

  <!-- Atrasos/Faltas -->
  <Card>
    <div class="section-header">
      <button
        class="section-toggle"
        aria-expanded={expandedSections['absence']}
        on:click={() => toggleSection('absence')}
      >
        <span class="section-title">Jornadas mais curtas e Faltas</span>
        <span class="section-toggle__icon" aria-hidden="true">
          {expandedSections['absence'] ? '▲' : '▼'}
        </span>
      </button>
    </div>
    {#if expandedSections['absence']}
      <div class="section-body">
        <!-- Disclaimer obrigatório (E3): faltas inferidas são heurística -->
        <div class="absence-disclaimer" role="note" data-testid="absence-disclaimer">
          <span class="absence-disclaimer__icon" aria-hidden="true">⚠</span>
          <p class="absence-disclaimer__text">
            <strong>Faltas inferidas</strong> são estimadas a partir da ausência de marcação.
            Podem incluir <strong>atestados</strong> curtos, folgas autorizadas ou falhas no
            relógio de ponto. Os blocos longos (5+ dias consecutivos) são separados como
            afastamentos. <strong>Verifique</strong> antes de planejar ações disciplinares
            ou descontos.
          </p>
        </div>

        <!-- Gráfico de barras agrupadas: % faltas e % jornadas curtas por dia -->
        {#if absenceChartPoints.length > 0}
          <div class="absence-chart-wrapper">
            <AbsenceChart points={absenceChartPoints} kpis={absenceChartKpis} />
          </div>
        {/if}

        <h4 class="metrics-grid__subtitle">Totais do período</h4>
        <div class="metrics-grid">
          <MetricTile
            label={metrics.absence.shortJourneys.label}
            value={formatMetricValue(metrics.absence.shortJourneys)}
            helper={metrics.absence.shortJourneys.helper}
            status={metrics.absence.shortJourneys.value > 0 ? 'warning' : 'ok'}
            drillDownId={metrics.absence.shortJourneys.code}
            on:click={() => openDrillDown(metrics.absence.shortJourneys.code)}
          />
          <MetricTile
            label={metrics.absence.shortJourneyMinutes.label}
            value={formatMetricValue(metrics.absence.shortJourneyMinutes)}
            helper={metrics.absence.shortJourneyMinutes.helper}
            status={metrics.absence.shortJourneyMinutes.value > 0 ? 'warning' : 'ok'}
          />
          <MetricTile
            label="Faltas isoladas"
            value={`${metrics.absence.faltasIsolated.value} (${metrics.absence.faltasIsolated.affectedEmployees} colaboradores)`}
            helper="FALTAS RECUPERÁVEIS · 1–4 dias úteis consecutivos sem ponto. Comportamento típico de falta não-justificada — passível de revisão com gestor."
            status={metrics.absence.faltasIsolated.value > 0 ? 'warning' : 'ok'}
            drillDownId={metrics.absence.faltas.code}
            on:click={() => openDrillDown(metrics.absence.faltas.code)}
          />
          <MetricTile
            label="Afastamentos longos"
            value={`${metrics.absence.leaveBlocks.totalDays} dias (${metrics.absence.leaveBlocks.affectedEmployees} colaboradores)`}
            helper="AFASTAMENTOS · NÃO RECUPERÁVEL · Blocos de 5+ dias úteis consecutivos sem ponto. Provável atestado, licença ou férias — pago por lei, não é custo recuperável."
            status="neutral"
          />
        </div>

        <h4 class="metrics-grid__subtitle">Por colaborador / mês (30 dias)</h4>
        <div class="metrics-grid">
          <MetricTile
            label="Jornadas mais curtas / colaborador / mês"
            value={formatNum(metrics.absence.shortJourneysPerEmployeeMonth.value)}
            helper={metrics.absence.shortJourneysPerEmployeeMonth.helper}
            status="neutral"
          />
          <MetricTile
            label="Faltas / colaborador / mês"
            value={formatNum(metrics.absence.faltasPerEmployeeMonth.value)}
            helper={metrics.absence.faltasPerEmployeeMonth.helper}
            status="neutral"
          />
        </div>
      </div>
    {/if}
  </Card>

  <!-- Retrabalho -->
  <Card>
    <div class="section-header">
      <button
        class="section-toggle"
        aria-expanded={expandedSections['rework']}
        on:click={() => toggleSection('rework')}
      >
        <span class="section-title">Retrabalho</span>
        <span class="section-toggle__icon" aria-hidden="true">
          {expandedSections['rework'] ? '▲' : '▼'}
        </span>
      </button>
    </div>
    {#if expandedSections['rework']}
      <div class="section-body">
        <div class="metrics-grid">
          <MetricTile
            label={metrics.rework.inferredManualPunches.label}
            value={formatMetricValue(metrics.rework.inferredManualPunches)}
            helper={metrics.rework.inferredManualPunches.helper}
            status={metrics.rework.inferredManualPunches.value > 0 ? 'warning' : 'ok'}
          />
          <MetricTile
            label={metrics.rework.inferredDisregardedPunches.label}
            value={formatMetricValue(metrics.rework.inferredDisregardedPunches)}
            helper={metrics.rework.inferredDisregardedPunches.helper}
            status={metrics.rework.inferredDisregardedPunches.value > 0 ? 'warning' : 'ok'}
          />
          <MetricTile
            label={metrics.rework.adjustmentRate.label}
            value={formatMetricValue(metrics.rework.adjustmentRate)}
            helper={metrics.rework.adjustmentRate.helper}
            status={metrics.rework.adjustmentRate.value > 20 ? 'error' : metrics.rework.adjustmentRate.value > 5 ? 'warning' : 'ok'}
          />
          <MetricTile
            label={metrics.rework.correctionTime.label}
            value={formatMetricValue(metrics.rework.correctionTime)}
            helper={metrics.rework.correctionTime.helper}
            status={metrics.rework.correctionTime.value > 0 ? 'warning' : 'ok'}
          />
          {#if metrics.rework.correctionCost !== null}
            <MetricTile
              label={metrics.rework.correctionCost.label}
              value={formatMetricValue(metrics.rework.correctionCost)}
              helper={metrics.rework.correctionCost.helper}
              status="error"
            />
          {/if}
        </div>
      </div>
    {/if}
  </Card>

  <!-- Risco -->
  <Card>
    <div class="section-header">
      <button
        class="section-toggle"
        aria-expanded={expandedSections['risk']}
        on:click={() => toggleSection('risk')}
      >
        <span class="section-title">Risco</span>
        <span class="section-toggle__icon" aria-hidden="true">
          {expandedSections['risk'] ? '▲' : '▼'}
        </span>
      </button>
    </div>
    {#if expandedSections['risk']}
      <div class="section-body">
        {#if metrics.risk.topRiskSignal}
          <div class="top-risk">
            <span class="top-risk__badge">SINAL MAIS RELEVANTE</span>
            <h4 class="top-risk__title">{metrics.risk.topRiskSignal.label}</h4>
            <p class="top-risk__description">{metrics.risk.topRiskSignal.description}</p>
            <p class="top-risk__counts">
              <strong>{metrics.risk.topRiskSignal.occurrenceCount}</strong> ocorrência{metrics.risk.topRiskSignal.occurrenceCount !== 1 ? 's' : ''}
              em <strong>{metrics.risk.topRiskSignal.affectedEmployees}</strong> colaborador{metrics.risk.topRiskSignal.affectedEmployees !== 1 ? 'es' : ''}
            </p>
          </div>
        {/if}

        {#if metrics.risk.totalDaysWithCorrections}
          <div class="metrics-grid">
            <MetricTile
              label={metrics.risk.totalDaysWithCorrections.label}
              value={formatMetricValue(metrics.risk.totalDaysWithCorrections)}
              helper={metrics.risk.totalDaysWithCorrections.helper}
              status={metrics.risk.totalDaysWithCorrections.value > 0 ? 'warning' : 'ok'}
            />
          </div>
        {/if}

        {#if metrics.risk.inconsistentPairs.length === 0 &&
             metrics.risk.excessiveJourneys.length === 0 &&
             metrics.risk.shortIntradayBreaks.length === 0 &&
             metrics.risk.shortInterjourneyBreaks.length === 0 &&
             metrics.risk.potentialBritishPoint.length === 0 &&
             metrics.risk.documentIntegrity.length === 0 &&
             metrics.risk.chronicOvertime.length === 0 &&
             metrics.risk.shortIntradayBreakRecurrence.length === 0}
          <EmptyState message="Nenhum sinal de risco detectado no período analisado." />
        {:else}
          <ul class="risk-signal-list">
            {#each [...metrics.risk.inconsistentPairs,
                    ...metrics.risk.excessiveJourneys,
                    ...metrics.risk.shortIntradayBreaks,
                    ...metrics.risk.shortInterjourneyBreaks,
                    ...metrics.risk.potentialBritishPoint,
                    ...metrics.risk.documentIntegrity,
                    ...metrics.risk.chronicOvertime,
                    ...metrics.risk.shortIntradayBreakRecurrence] as signal (signal.code)}
              <li class="risk-signal risk-signal--{signal.confidence}">
                <span class="risk-signal__badge">{signal.confidence}</span>
                <div class="risk-signal__content">
                  <p class="risk-signal__message">{signal.message}</p>
                  <p class="risk-signal__explanation">{signal.explanation}</p>
                  <p class="risk-signal__occurrences">{signal.occurrenceCount} ocorrência{signal.occurrenceCount !== 1 ? 's' : ''}</p>
                  {#if 'notDetectable' in signal && signal.notDetectable}
                    <p class="risk-signal__note">⚠ Não detectável: requer arquivo AEJ. Em AFD-only, este sinal não pode ser avaliado.</p>
                  {/if}
                </div>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    {/if}
  </Card>

</div>

<style>
  .metrics-sections {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-4);
  }

  /* E3: banner de disclaimer obrigatório no card "Atrasos e Faltas" */
  .absence-disclaimer {
    display: flex;
    gap: var(--ptt-space-3, 12px);
    align-items: flex-start;
    background: var(--ptt-yellow-50, #FFF8E1);
    border-left: 4px solid var(--ptt-yellow-200, #F9C200);
    padding: var(--ptt-space-3, 12px) var(--ptt-space-4, 16px);
    border-radius: var(--ptt-radius-sm, 4px);
    margin-bottom: var(--ptt-space-4, 16px);
  }

  .absence-disclaimer__icon {
    flex-shrink: 0;
    font-size: 1.125rem;
    line-height: 1.4;
    color: var(--ptt-yellow-300, #B38600);
  }

  .absence-disclaimer__text {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--ptt-gray-300, #323232);
  }

  /* E2: trio Saldo Bruto / Excesso / Déficit no card "Custo" */
  .balance-trio {
    border: 1px solid var(--ptt-purple-50, #E8E1FB);
    border-left: 4px solid var(--ptt-purple-100, #5B2FD4);
    padding: var(--ptt-space-3, 12px) var(--ptt-space-4, 16px);
    border-radius: var(--ptt-radius-sm, 4px);
    margin-bottom: var(--ptt-space-4, 16px);
  }

  .balance-trio__intro {
    margin: 0 0 var(--ptt-space-3, 12px) 0;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: var(--ptt-gray-300, #323232);
  }

  .balance-trio__grid {
    /* herda do .metrics-grid */
  }

  .section-header {
    margin-bottom: 0;
  }

  .section-toggle {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    text-align: left;
  }

  .section-toggle:focus-visible {
    outline: 2px solid var(--ptt-purple-100);
    outline-offset: 2px;
    border-radius: var(--ptt-radius-sm);
  }

  .section-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--ptt-gray-300);
  }

  .section-toggle__icon {
    font-size: 0.75rem;
    color: var(--ptt-gray-100);
  }

  .section-body {
    margin-top: var(--ptt-space-4);
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--ptt-space-3);
  }

  /* Risk signals */
  .risk-signal-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-3);
  }

  .risk-signal {
    display: flex;
    gap: var(--ptt-space-3);
    padding: var(--ptt-space-3);
    border-radius: var(--ptt-radius-md);
    background: var(--ptt-gray-10);
  }

  .risk-signal__badge {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: var(--ptt-space-1) var(--ptt-space-2);
    border-radius: var(--ptt-radius-sm);
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    height: fit-content;
  }

  .risk-signal--high .risk-signal__badge {
    background: var(--ptt-orange-100);
    color: var(--ptt-white);
  }

  .risk-signal--medium .risk-signal__badge {
    background: var(--ptt-yellow-200);
    color: var(--ptt-black);
  }

  .risk-signal--low .risk-signal__badge {
    background: var(--ptt-gray-20);
    color: var(--ptt-gray-200);
  }

  .risk-signal__content {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-1);
  }

  .risk-signal__message {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--ptt-gray-300);
    margin: 0;
  }

  .risk-signal__explanation {
    font-size: 0.75rem;
    color: var(--ptt-gray-100);
    margin: 0;
  }

  .risk-signal__occurrences {
    font-size: 0.75rem;
    color: var(--ptt-gray-100);
    margin: 0;
  }

  @media (max-width: 768px) {
    .metrics-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: 480px) {
    .metrics-grid {
      grid-template-columns: 1fr;
    }
  }

  .metrics-sections__holiday-note {
    margin-top: var(--ptt-space-3);
    font-family: var(--ptt-font-sans);
    font-size: 0.8125rem;
    color: var(--ptt-gray-200);
    line-height: 1.4;
    border-top: 1px solid var(--ptt-gray-20);
    padding-top: var(--ptt-space-2);
  }

  .metrics-sections__cost-cta {
    margin-top: var(--ptt-space-3);
    padding: var(--ptt-space-3) var(--ptt-space-4);
    background: var(--ptt-purple-10, #f0ebfd);
    border-left: 3px solid var(--ptt-purple-100, #5B2FD4);
    border-radius: var(--ptt-radius-sm);
    font-family: var(--ptt-font-sans);
    font-size: 0.875rem;
    color: var(--ptt-gray-300);
    line-height: 1.45;
  }

  .metrics-sections__cost-link {
    display: inline;
    background: none;
    border: none;
    padding: 0;
    margin-left: var(--ptt-space-2);
    color: var(--ptt-purple-100, #5B2FD4);
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
  }

  .metrics-sections__cost-link:hover {
    opacity: 0.8;
  }

  .metrics-grid__subtitle {
    font-size: 0.8125rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
    color: var(--ptt-gray-200, #555);
    margin: var(--ptt-space-4) 0 var(--ptt-space-2);
  }

  .top-risk {
    background: linear-gradient(0deg, var(--ptt-orange-10, #fff7ed), transparent);
    border-left: 4px solid var(--ptt-orange-100, #fb7224);
    padding: var(--ptt-space-4) var(--ptt-space-5);
    border-radius: var(--ptt-radius-md);
    margin-bottom: var(--ptt-space-4);
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-2);
  }

  .top-risk__badge {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 700;
    color: var(--ptt-orange-100, #fb7224);
  }

  .top-risk__title {
    font-size: 1.125rem;
    font-weight: 700;
    margin: 0;
    color: var(--ptt-gray-300);
  }

  .top-risk__description {
    margin: 0;
    font-size: 0.9375rem;
    color: var(--ptt-gray-200, #555);
    line-height: 1.4;
  }

  .top-risk__counts {
    margin: 0;
    font-size: 0.875rem;
    color: var(--ptt-gray-200, #555);
  }

  .risk-signal__note {
    margin: var(--ptt-space-1) 0 0;
    font-size: 0.75rem;
    color: var(--ptt-orange-100, #fb7224);
    font-style: italic;
  }

  /* AbsenceChart wrapper */
  .absence-chart-wrapper {
    margin-bottom: var(--ptt-space-5);
    border: 1px solid var(--ptt-gray-20, #e7e7e7);
    border-radius: var(--ptt-radius-md);
    padding: var(--ptt-space-4);
    background: var(--ptt-white, #fff);
  }
</style>
